<script lang="ts" setup>
const { $t, $getLocale } = useI18n()
const locale = $getLocale()

const { public: config } = useRuntimeConfig()

// Added this to handle removal of utm_* parameters by varnish
// see https://github.com/nuxt/nuxt/issues/23153
await useAsyncData('data',
  async (nuxtApp) => {
    delete nuxtApp?.payload?.path
    return {}
  },
)

const { initCartWatchers } = useCart()
const { fetchStoreStock, cartSkuSignature } = useStorelocator()
const { fetchStoreData } = useStore()
await fetchStoreData()

onMounted(() => {
  initCartWatchers()
  // store_stock is expensive to resolve, so we only refetch it when the cart's
  // SKU signature actually changes (e.g. an item is added/removed), not on every
  // mutation or page navigation. cartSkuSignature stays stable if only
  // quantities change, so this won't refetch unnecessarily.
  watch(cartSkuSignature, () => {
    fetchStoreStock()
  }, { immediate: true })
})

useSeoMeta({
  title: $t('meta.name') as string,
  // titleTemplate: `%s | ${siteConfig.name}`,
  description: $t('meta.description') as string,
  formatDetection: 'telephone=no',
})

useHeadSafe({
  htmlAttrs: {
    lang: locale,
  },
  link: [
    { rel: 'icon', type: 'image/png', href: '/favicon-96x96.png', sizes: '69x69' },
    { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
    { rel: 'shortcut icon', href: '/favicon.ico' },
    { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
    { rel: 'manifest', href: '/site.webmanifest' },
    { rel: 'preconnect', href: config.xcdnUrl },
  ],
  meta: [
    { name: 'apple-mobile-web-app-title', content: $t('meta.name') as string },
  ],
})

let youShouldAsk = `<`
youShouldAsk += `script type="text/javascript" async src="${config.youshouldask}"><`
youShouldAsk += `/script>`

const youShouldAskLoaded = ref(false)
provide('youShouldAskLoaded', youShouldAskLoaded)

const embedOnLoad = () => {
  youShouldAskLoaded.value = true
}
</script>

<template>
  <div
    v-if="$route.path.startsWith('/embed/')"
    class="app app--embed"
  >
    <NuxtLayout class="app__layout">
      <NuxtPage class="app__template" />
    </NuxtLayout>
  </div>
  <div
    v-else
    class="app"
  >
    <NuxtRouteAnnouncer />

    <a
      class="layout__skip-link skip-link"
      href="#main"
    >
      {{ $t('Skip to main content') }}
    </a>

    <AppNotification />

    <NuxtLayout class="app__layout">
      <NuxtPage class="app__template" />
    </NuxtLayout>

    <ThirdpartyLoader :loadtime="7500" />

    <UiEmbed
      :embed-code="youShouldAsk"
      persist
      @load="embedOnLoad"
    />

    <LazyDevtools />
  </div>
</template>

<style>
@import url("~layers/assets/css/vars.css");
@import url("~layers/assets/css/tokens/colors.css");
@import url("~layers/assets/css/base/reset.css");
@import url("~layers/assets/css/base/root-and-sectioning.css");
@import url("~layers/assets/css/base/forms-and-inputs.css");
@import url("~layers/assets/css/base/forms-and-inputs__file.css");
@import url("~layers/assets/css/base/forms-and-inputs__color.css");
@import url("~layers/assets/css/base/forms-and-inputs__range.css");
@import url("~layers/assets/css/base/forms-and-inputs__select.css");
@import url("~layers/assets/css/base/forms-and-inputs__checkbox-radio.css");
@import url("~layers/assets/css/base/text-content.css");
@import url("~layers/assets/css/base/text-content__headings.css");
@import url("~layers/assets/css/base/text-content__anchor-button.css");
@import url("~layers/assets/css/base/text-content__kbd.css");
@import url("~layers/assets/css/base/text-content__ruby.css");
@import url("~layers/assets/css/base/multimedia-and-embeds.css");
@import url("~layers/assets/css/base/lists.css");
@import url("~layers/assets/css/base/tables.css");
@import url("~layers/assets/css/base/details-summary.css");
@import url("~layers/assets/css/base/dialog-popover-backdrop.css");
@import url("~layers/assets/css/base/meter.css");
@import url("~layers/assets/css/base/progress.css");
@import url("~layers/assets/css/transitions/slide-down.css");
@import url("~layers/assets/css/keyframes/percentage.css");
@import url("~layers/assets/css/keyframes/reveal.css");
@import url("~layers/assets/css/vue-formkit.css");
@import url("~layers/app.css");

:root {
  --offset-ysa: 0;
}

body:has(.filter-list__toggle--sticky, .add-to-cart-sticky__button, .cart-cta) {
  --offset-ysa: 60px;
}

.ysa_hided_popup_div,
.ysa_status_hided {
  margin-block-end: var(--offset-ysa);
}

.ysa_status_hided {
  background-color: var(--color-signals-success) !important;
}

#teleports > div {
  display: none;
}
</style>
